import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

function SignUp() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const handleSignUp = async () => {
    setLoading(true);
    setError("");

    // Validation
    if (!firstName.trim()) {
      setError("First name is required");
      setLoading(false);
      return;
    }
    if (!lastName.trim()) {
      setError("Last name is required");
      setLoading(false);
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      setLoading(false);
      return;
    }
    if (!password) {
      setError("Password is required");
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    const { data, error: authError } = await signUp(email, password, firstName, lastName);

    setLoading(false);

    if (authError) {
      setError(authError.message);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="themed-background" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px'
    }}>
      {/* Main Sign Up Form */}
      <div className="themed-surface" style={{
        width: '100%',
        maxWidth: '500px',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px var(--shadow-color)',
        border: '1px solid var(--border-color)'
      }}>
        <h2 style={{
          textAlign: 'center',
          marginBottom: '30px',
          color: 'var(--color-surface-text)',
          fontSize: '24px',
          fontWeight: '600'
        }}>
          Create Your Account
        </h2>

        {error && (
          <div style={{
            color: '#dc3545',
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Name Fields Row */}
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="firstName" style={{
                display: 'block',
                marginBottom: '8px',
                color: 'var(--color-surface-text)',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="form-input"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="lastName" style={{
                display: 'block',
                marginBottom: '8px',
                color: 'var(--color-surface-text)',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="form-input"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" style={{
              display: 'block',
              marginBottom: '8px',
              color: 'var(--color-surface-text)',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Email Address *
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="themed-input"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
            />
          </div>

          <div>
            <label htmlFor="password" style={{
              display: 'block',
              marginBottom: '8px',
              color: 'var(--color-surface-text)',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Password *
            </label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="themed-input"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" style={{
              display: 'block',
              marginBottom: '8px',
              color: 'var(--color-surface-text)',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="themed-input"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
            />
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '10px'
          }}>
            <button
              type="button"
              onClick={handleSignUp}
              disabled={loading}
              className="themed-button"
              style={{
                width: '100%',
                padding: '14px 20px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              style={{
                width: '100%',
                padding: '12px 20px',
                borderRadius: '8px',
                border: '2px solid var(--color-primary)',
                backgroundColor: 'transparent',
                color: 'var(--color-primary)',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary)';
                e.target.style.color = 'var(--color-primary-text)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--color-primary)';
              }}
            >
              Already have an account? Sign In
            </button>
          </div>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          color: 'var(--color-outline)',
          fontSize: '14px'
        }}>
          Join our learning community today!
        </div>
      </div>
    </div>
  );
}

export default SignUp;
